import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { FiSave, FiArrowLeft, FiLoader, FiUser, FiMail, FiPhone, FiShield, FiTrash2, FiLock } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { administradoresAPI } from '../services/api';
import theme from '../styles/theme';
import Layout from '../components/Layout';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
`;

const BackButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin: 0;
`;

const Form = styled.form`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const SectionTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const Required = styled.span`
  color: ${theme.colors.error[500]};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const CheckboxLabel = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  cursor: pointer;
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  justify-content: flex-end;
  padding-top: ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border.primary};
`;

const Button = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return theme.colors.border.primary;
  }};
  background: ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return 'transparent';
  }};
  color: ${props => {
    if (props.$danger || props.$primary) return theme.colors.text.primary;
    return theme.colors.text.secondary;
  }};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  min-width: 120px;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.background.primary;
    }};
    color: ${props => {
      if (props.$danger || props.$primary) return theme.colors.text.primary;
      return theme.colors.primary[500];
    }};
    border-color: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.primary[500];
    }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonIcon = styled.div`
  font-size: ${theme.fontSizes.base};
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error[500]}20;
  border: 1px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.error[600]};
  font-size: ${theme.fontSizes.sm};
`;

const SuccessMessage = styled.div`
  background: ${theme.colors.success[500]}20;
  border: 1px solid ${theme.colors.success[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.success[600]};
  font-size: ${theme.fontSizes.sm};
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h3`
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[4]} 0;
`;

const ModalMessage = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  line-height: 1.5;
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => props.$danger ? theme.colors.error[500] : theme.colors.border.primary};
  background: ${props => props.$danger ? theme.colors.error[500] : 'transparent'};
  color: ${props => props.$danger ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${props => props.$danger ? theme.colors.error[600] : theme.colors.background.secondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const AdministradorForm = ({ primeiroAdmin = false }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = id && id !== 'novo';

  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    telefone: '',
    senha: '',
    ativo: true,
  });

  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditing);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmSenha, setConfirmSenha] = useState('');

  // Máscara de telefone brasileira (formato: (11) 99999-9999)
  const formatTelefone = (value) => {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 11);
    if (!digits) return '';
    if (digits.length <= 2) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0,2)}) ${digits.slice(2)}`;
    if (digits.length <= 10) return `(${digits.slice(0,2)}) ${digits.slice(2,6)}-${digits.slice(6)}`;
    return `(${digits.slice(0,2)}) ${digits.slice(2,7)}-${digits.slice(7)}`;
  };

  useEffect(() => {
    if (isEditing) {
      carregarAdministrador();
    }
  }, [id]);

  const carregarAdministrador = async () => {
    try {
      setLoadingData(true);
      const response = await administradoresAPI.buscarPorId(id);
      if (response.data.success) {
        const admin = response.data.data;
        setFormData({
          nome: admin.nome || '',
          email: admin.email || '',
          telefone: admin.telefone || '',
          senha: '',
          ativo: Boolean(admin.ativo),
        });
      } else {
        setError('Erro ao carregar dados do administrador');
      }
    } catch (err) {
      console.error('Erro ao carregar administrador:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoadingData(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      if (!formData.nome.trim()) throw new Error('Nome é obrigatório');
      if (!formData.email.trim()) throw new Error('Email é obrigatório');
      if (!isEditing && !formData.senha.trim()) throw new Error('Senha é obrigatória para novo administrador');

      // Validar senha forte (na criação e na atualização quando informada)
      const strongPasswordRegex = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/;
      if ((!isEditing && !strongPasswordRegex.test(formData.senha)) || (isEditing && formData.senha && !strongPasswordRegex.test(formData.senha))) {
        throw new Error('A senha deve conter letra, número e símbolo.');
      }

      // Confirmar senha
      if (!isEditing) {
        if (!confirmSenha.trim()) throw new Error('Confirme a senha.');
        if (formData.senha !== confirmSenha) throw new Error('As senhas não conferem.');
      } else if (isEditing && formData.senha) {
        if (!confirmSenha.trim()) throw new Error('Confirme a nova senha.');
        if (formData.senha !== confirmSenha) throw new Error('As senhas não conferem.');
      }

      const payload = {
        nome: formData.nome.trim(),
        email: formData.email.trim(),
        telefone: formData.telefone?.trim() || null,
        ativo: Boolean(formData.ativo),
      };
      if (!isEditing && formData.senha && formData.senha.trim()) payload.senha = formData.senha.trim();
      if (isEditing && formData.senha && formData.senha.trim()) payload.senha = formData.senha.trim(); // atualização opcional

      let response;
      if (isEditing) {
        response = await administradoresAPI.atualizar(id, payload);
      } else if (primeiroAdmin) {
        response = await administradoresAPI.criarPrimeiro(payload);
      } else {
        response = await administradoresAPI.criar(payload);
      }

      if (response.data.success) {
        setSuccess(isEditing ? 'Administrador atualizado com sucesso!' : 'Administrador criado com sucesso!');
        setTimeout(() => navigate('/administradores'), 1200);
      } else {
        setError(response.data.error || 'Erro ao salvar administrador');
      }
    } catch (err) {
      console.error('Erro ao salvar administrador:', err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Erro ao conectar com o servidor. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVoltar = () => {
    navigate('/administradores');
  };

  const handleExcluir = async () => {
    try {
      setDeleting(true);
      setError(null);
      await administradoresAPI.deletar(id);
      navigate('/administradores');
    } catch (err) {
      console.error('Erro ao excluir administrador:', err);
      setError('Erro ao excluir administrador');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const abrirModalExclusao = () => setShowDeleteModal(true);
  const fecharModalExclusao = () => setShowDeleteModal(false);

  if (loadingData) {
    return (
      <Layout title="Carregando...">
        <Container>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
            <FiLoader style={{ fontSize: '2rem', animation: 'spin 1s linear infinite' }} />
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={isEditing ? 'Editar Administrador' : (primeiroAdmin ? 'Primeiro Administrador' : 'Novo Administrador')}>
      <Container>
        <Header>
          <BackButton onClick={handleVoltar}>
            <FiArrowLeft />
            Voltar
          </BackButton>
          <Title>{isEditing ? 'Editar Administrador' : 'Novo Administrador'}</Title>
        </Header>

        <Form onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <FormSection>
            <SectionTitle>
              <FiUser />
              Informações
            </SectionTitle>
            <FormGrid>
              <FormGroup>
                <Label>Nome <Required>*</Required></Label>
                <Input type="text" placeholder="Nome completo" value={formData.nome} onChange={(e) => handleInputChange('nome', e.target.value)} required />
              </FormGroup>
              <FormGroup>
                <Label>Email <Required>*</Required></Label>
                <Input type="email" placeholder="email@exemplo.com" value={formData.email} onChange={(e) => handleInputChange('email', e.target.value)} required />
              </FormGroup>
              <FormGroup>
                <Label>Telefone</Label>
                <Input
                  type="tel"
                  placeholder="(11) 99999-9999"
                  value={formData.telefone}
                  onChange={(e) => handleInputChange('telefone', formatTelefone(e.target.value))}
                />
              </FormGroup>
              {!isEditing && (
                <FormGroup>
                  <Label>Senha <Required>*</Required></Label>
                  <Input type="password" placeholder="Defina uma senha (letra, número e símbolo)" value={formData.senha} onChange={(e) => handleInputChange('senha', e.target.value)} />
                </FormGroup>
              )}
              {!isEditing && (
                <FormGroup>
                  <Label>Confirmar senha <Required>*</Required></Label>
                  <Input type="password" placeholder="Repita a senha" value={confirmSenha} onChange={(e) => setConfirmSenha(e.target.value)} />
                </FormGroup>
              )}
              {isEditing && (
                <FormGroup>
                  <Label>Nova senha</Label>
                  <Input type="password" placeholder="Deixe em branco para manter (letra, número e símbolo)" value={formData.senha} onChange={(e) => handleInputChange('senha', e.target.value)} />
                </FormGroup>
              )}
              {isEditing && (
                <FormGroup>
                  <Label>Confirmar nova senha</Label>
                  <Input type="password" placeholder="Repita a nova senha" value={confirmSenha} onChange={(e) => setConfirmSenha(e.target.value)} />
                </FormGroup>
              )}
            </FormGrid>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <FiShield />
              Configurações
            </SectionTitle>
            <CheckboxGroup>
              <Checkbox id="ativo" type="checkbox" checked={formData.ativo} onChange={(e) => handleInputChange('ativo', e.target.checked)} />
              <CheckboxLabel htmlFor="ativo">Administrador ativo</CheckboxLabel>
            </CheckboxGroup>
          </FormSection>

          <Actions>
            <Button type="button" onClick={handleVoltar}>Cancelar</Button>
            {isEditing && (
              <Button type="button" onClick={abrirModalExclusao} $danger>
                <ButtonIcon><FiTrash2 /></ButtonIcon>
                Excluir
              </Button>
            )}
            <Button $primary type="submit" disabled={loading}>
              <ButtonIcon>
                {loading ? <FiLoader /> : <FiSave />}
              </ButtonIcon>
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </Actions>
        </Form>
      </Container>

      {showDeleteModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Excluir Administrador</ModalTitle>
            <ModalMessage>
              Tem certeza que deseja excluir este administrador? Esta ação não pode ser desfeita.
            </ModalMessage>
            <ModalActions>
              <ModalButton onClick={fecharModalExclusao}>Cancelar</ModalButton>
              <ModalButton $danger onClick={handleExcluir} disabled={deleting}>
                <FiLock />
                {deleting ? 'Excluindo...' : 'Excluir'}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Layout>
  );
};

export default AdministradorForm;


